// Clock-in / Clock-out CTA on the Home hero card (T-259).
//
// Replaces the persistent `<HeaderClockInPill>` that used to live in
// the App-Shell header. Same data flow:
//   - `GET /api/employees/:meId/attendance/state` for the current
//     `{ clockState, openEntry }` — cached + polled every 60 s so the
//     label stays fresh.
//   - `POST /api/employees/:meId/attendance/clock` to flip the state;
//     optimistically writes the response back into the cache so the
//     label flips immediately.
//   - Re-renders every 30 s while clocked-in so the live duration
//     label (`Clocked in · 2h 14m`) ticks forward.
//
// The button is hero-styled (semi-transparent white background, white
// border + text) to sit alongside the other two CTAs ("Go to profile",
// "Request time off"). When the user is clocked in, swap to a more
// saturated white/25 background + emerald dot so the state is obvious
// at a glance.
//
// `employeeId={null}` (bootstrap users, no roster row yet) renders a
// disabled button with a "Add yourself to the roster" tooltip — same
// affordance pattern as the rest of the hero CTAs.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock as ClockIcon } from 'lucide-react';
import type {
  AttendanceClockResponse,
  AttendanceStateResponse,
} from '@pa/shared';
import { api } from '../../api/client';

const TICK_INTERVAL_MS = 30_000;
const POLL_INTERVAL_MS = 60_000;

export interface ClockInButtonProps {
  employeeId: string | null;
}

export function ClockInButton({ employeeId }: ClockInButtonProps) {
  if (!employeeId) {
    return (
      <button
        type="button"
        disabled
        data-testid="home-hero-clock-cta"
        data-cta="clock_in"
        className="bg-white/20 text-white/70 px-5 py-2 rounded-xl text-sm font-bold cursor-not-allowed border border-white/30"
        title="Add yourself to the roster from the People view to enable this action."
      >
        <span className="inline-flex items-center gap-2">
          <ClockIcon size={14} aria-hidden />
          <span>Clock in</span>
        </span>
      </button>
    );
  }
  return <Inner employeeId={employeeId} />;
}

function Inner({ employeeId }: { employeeId: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['attendance-state', employeeId] as const;

  const query = useQuery({
    queryKey,
    queryFn: () =>
      api.get<AttendanceStateResponse>(
        `/api/employees/${employeeId}/attendance/state`,
      ),
    refetchInterval: POLL_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  const toggle = useMutation({
    mutationFn: () =>
      api.post<AttendanceClockResponse>(
        `/api/employees/${employeeId}/attendance/clock`,
        {},
      ),
    onSuccess: (res) => {
      queryClient.setQueryData<AttendanceStateResponse>(queryKey, {
        clockState: res.clockState,
        openEntry: res.clockState === 'clocked_in' ? res.entry : null,
      });
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['attendance-tab', employeeId],
      });
    },
  });

  // Re-render every 30s so the live duration label stays fresh while
  // clocked in. Cheap — just a small state bump that triggers a
  // re-render; the data itself is unchanged between server polls.
  const [tick, setTick] = useState(0);
  const isOpen = query.data?.clockState === 'clocked_in';
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setTick((t) => t + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOpen]);
  // Reference `tick` so React knows to re-evaluate the duration label.
  void tick;

  // Suspend the button while the first state probe is in flight so
  // the label doesn't flash "Clock in" when the user is actually
  // already clocked in.
  if (query.isLoading || query.data === undefined) {
    if (query.isError) return null;
    return (
      <div
        aria-hidden
        className="w-28 h-10 rounded-xl bg-white/15 animate-pulse"
        data-testid="home-hero-clock-skeleton"
      />
    );
  }

  // Errors land silently — the button disappears so the rest of the
  // hero keeps working. Don't block the UI for an attendance hiccup.
  if (query.isError) return null;

  const label = isOpen
    ? `Clocked in · ${formatLiveDuration(query.data.openEntry?.start ?? null)}`
    : 'Clock in';

  return (
    <button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      data-testid="home-hero-clock-cta"
      data-cta="clock_in"
      data-clock-state={query.data.clockState}
      aria-label={isOpen ? 'Clock out' : 'Clock in'}
      className={
        'px-5 py-2 rounded-xl text-sm font-bold border transition active:scale-95 disabled:opacity-50 ' +
        (isOpen
          ? 'bg-white/25 hover:bg-white/35 border-white/40 text-white'
          : 'bg-white/15 hover:bg-white/25 border-white/30 text-white')
      }
    >
      <span className="inline-flex items-center gap-2">
        {isOpen ? (
          <span
            aria-hidden
            className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"
            data-testid="home-hero-clock-dot"
          />
        ) : (
          <ClockIcon size={14} aria-hidden />
        )}
        <span>{label}</span>
      </span>
    </button>
  );
}

/**
 * Format the duration since `startIso` as `Hh Mm` (e.g. `2h 14m`).
 *
 * Defensive: returns `0m` when the input is null / unparseable / in
 * the future (clock-skew on the user's machine).
 *
 * Exported so unit tests can drive every code path without rendering.
 */
export function formatLiveDuration(startIso: string | null): string {
  if (!startIso) return '0m';
  const ms = Date.now() - new Date(startIso).getTime();
  if (Number.isNaN(ms) || ms <= 0) return '0m';
  const totalMin = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}
